import {
  Box,
  Container,
  Heading,
  HStack,
  VStack,
  Breadcrumb,
  BreadcrumbItem,
  FormControl,
  Text,
  useDisclosure,
  useToast,
  FormHelperText,
} from "@chakra-ui/react";

import { ChevronRightIcon } from "@chakra-ui/icons";

//Components
import Header from "components/dashHeader";
import Sidemenu from "components/sidemenu";
// import Card from "components/Card/Card";
import Input from "components/Input/Input";
import Button from "components/Button/Button";
import Select from "components/Select/Select";
import BreadcrumbLink from "components/BreadcrumbLink/BreadcrumbLink";

import SubmitPin from "components/Modals/SubmitPin";
import SuccessModal from "components/Modals/Success";

import { useParams, useHistory, useLocation } from "react-router-dom";

import constant from "config/constant";
import toCoinLocale from "util/toCoinLocale";
import toPrecision from "util/toPrecision";

import useWalletBalance from "hooks/useWalletBalance";

import { useState, useMemo, useEffect } from "react";

import { useFundCardMutation } from "services/cardsApi";
import getError from "util/getError";
import currencyFormat from "util/formatter";

const FundCard = (props) => {
  const minimumAmount = 10;
  const maximumFundingAmount = 10_000;
  const currentPage = "Card";
  const fundFee = 1.5;

  const { id } = useParams();
  const { getBalance } = useWalletBalance();
  const [state, setState] = useState({});

  const toast = useToast();
  const history = useHistory();
  const location = useLocation();

  const searchParams = useMemo(
    () => new URLSearchParams(location?.search),
    [location]
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isSuccessOpen,
    onOpen: onSuccessOpen,
    onClose: onSuccessClose,
  } = useDisclosure();

  const [fundCard, { data, isLoading: isFunding, error, isError, isSuccess }] =
    useFundCardMutation();

  const handleState = (newState) => {
    setState({ ...state, ...newState });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onOpen();
  };

  const handleFundCard = (pin) => {
    fundCard({ id, coin: state?.wallet, amount: state?.amount, pin });
    onClose();
  };

  const handleSuccessModalClose = () => {
    onSuccessClose();
    history.replace(
      `/dashboard/cards/${id}?provider=${searchParams.get("provider")}`
    );
  };

  useEffect(() => {
    if (isSuccess && data) {
      onSuccessOpen();
    }

    if (isError && error) {
      toast({
        position: "bottom-right",
        title: "Error",
        description: getError(error?.data?.error),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }, [isSuccess, error, data, isError, onSuccessOpen, toast]);

  const isInsufficientBalance = useMemo(
    () => (state?.amount ?? 0) > +getBalance(state?.wallet),
    [state, getBalance]
  );

  const isSmallerThanMinimum = useMemo(
    () =>
      !!state?.wallet &&
      !!state?.amount &&
      (state?.amount ?? 0) < minimumAmount,
    [state, minimumAmount]
  );

  const isMoreThanMinimum = useMemo(
    () =>
      !!state?.wallet &&
      !!state?.amount &&
      +(state?.amount ?? 0) > maximumFundingAmount,
    [state]
  );

  const fundingFee = useMemo(
    () => !!state?.amount && +state?.amount * (fundFee / 100),
    [state?.amount]
  );

  const isDisabled = useMemo(
    () =>
      !(state?.amount && state?.wallet) ||
      isFunding ||
      isInsufficientBalance ||
      isSmallerThanMinimum ||
      isMoreThanMinimum,
    [
      state,
      isFunding,
      isInsufficientBalance,
      isSmallerThanMinimum,
      isMoreThanMinimum,
    ]
  );

  return (
    <Box>
      <Sidemenu currentPage={currentPage} logoutUser={props.logout} />
      <Header />

      <Box className="cards">
        <Box
          className="page-container"
          p={["6em 0 !important", "6.5em 1.5em !important"]}
        >
          <Container maxW="container.xl">
            <Box px={["24px", "0", "0"]}>
              <Heading fontSize="3xl">Cards</Heading>

              <Breadcrumb separator={<ChevronRightIcon color="gray.500" />}>
                <BreadcrumbItem>
                  <BreadcrumbLink to="/dashboard/cards">Cards</BreadcrumbLink>
                </BreadcrumbItem>

                <BreadcrumbItem>
                  <BreadcrumbLink to={`/dashboard/cards/${id}`}>
                    My Card
                  </BreadcrumbLink>
                </BreadcrumbItem>

                <BreadcrumbItem isCurrentPage>
                  <BreadcrumbLink isCurrentPage href="#">
                    Fund
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Breadcrumb>
            </Box>

            <Box
              bg="white"
              border="1px solid #8688942f"
              // minH="681px"
              w="calc(100% - 25vw)"
              //   w="fit-content"
              mt="32px"
              borderRadius="8px"
              p="48px 28px"
            >
              <VStack as="form" maxW="xs" m="0 auto" onSubmit={handleSubmit}>
                <FormControl>
                  <Input
                    isRequired
                    isInvalid={
                      isInsufficientBalance ||
                      isSmallerThanMinimum ||
                      isMoreThanMinimum
                    }
                    type="number"
                    label="Amount"
                    value={state?.amount ?? ""}
                    onChange={(e) => handleState({ amount: e.target.value })}
                    startAdornment={"$"}
                    endAdornment="USD"
                  />
                  {isInsufficientBalance && (
                    <FormHelperText fontSize="xs" color="red.400">
                      {toCoinLocale(state?.wallet)} wallet balance is
                      insufficient
                    </FormHelperText>
                  )}
                  {/* {isSmallerThanMinimum && ( */}
                  {state?.wallet && (
                    <FormHelperText
                      fontSize="xs"
                      color={isSmallerThanMinimum ? "red.400" : "initial"}
                    >
                      Minimum funding amount is{" "}
                      {toPrecision(minimumAmount, state?.wallet)}
                    </FormHelperText>
                  )}

                  {state?.wallet && isMoreThanMinimum && (
                    <FormHelperText
                      fontSize="xs"
                      color={isMoreThanMinimum ? "red.400" : "initial"}
                    >
                      Maximum funding amount is{" "}
                      {currencyFormat("usd").format(maximumFundingAmount)}
                    </FormHelperText>
                  )}
                  {/* )} */}
                  <FormHelperText fontSize="sm">
                    Funding fee({fundFee}%):{" "}
                    <Box as="strong">${Number(fundingFee).toFixed(2) ?? 0}</Box>
                  </FormHelperText>
                </FormControl>
                <FormControl mt="20px !important">
                  <Select
                    isRequired
                    label="Wallet"
                    // isInvalid={getBalance(state?.wallet) < 1}
                    placeholder="Select Wallet"
                    value={state?.wallet ?? ""}
                    onChange={(e) => handleState({ wallet: e.target.value })}
                  >
                    {constant.stableCoins.map((coin) => (
                      <option key={`${coin}-wallet`} value={coin}>
                        {toCoinLocale(coin)}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {state?.wallet && (
                  <FormControl>
                    <HStack fontSize="sm">
                      <Text>Available balance: </Text>
                      <Text
                        color={
                          !isInsufficientBalance ? "var(--primary)" : "red.400"
                        }
                      >
                        {toPrecision(getBalance(state?.wallet), state?.wallet)}
                      </Text>
                    </HStack>
                  </FormControl>
                )}

                <Button
                  isLoading={isFunding}
                  disabled={isDisabled}
                  w="100%"
                  minH="64px"
                  mt="72px !important"
                  type="submit"
                  // onClick={handleSubmit}
                >
                  Fund
                </Button>
              </VStack>
            </Box>
          </Container>
        </Box>

        <SubmitPin
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleFundCard}
        />

        <SuccessModal
          isOpen={isSuccessOpen}
          onClose={handleSuccessModalClose}
          title="Success"
          message={`Card has been successfully funded with ${toPrecision(
            +state?.amount,
            state?.wallet
          )}`}
        />
      </Box>
    </Box>
  );
};

export default FundCard;
